import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { forkJoin } from "rxjs";
import { SubSink } from "subsink";
import { ChaseIdComponent } from "../../../../shared/chase-grid/chase-id.component";
import { BasicGridComponent } from "../../../../shared/grid/basic-grid/basic-grid.component";
import { GridPipeName } from "../../../../shared/grid/grid-pipe.enum";
import { GridService } from "../../../../shared/grid/grid.service";
import { GridColumnDefinition } from "../../../../shared/grid/models/grid-column-definition.model";
import { GridConfiguration } from "../../../../shared/grid/models/grid-configuration.model";
import { FeedbackChallengeQueryItem } from "../../member/chase-detail/chase-detail-chart/overread-feedback/feedback-challenge-query-item.model";
import { FeedbackChallengeQuerySearch } from "../../member/chase-detail/chase-detail-chart/overread-feedback/feedback-challenge-query-search.model";
import { OverreadFeedbackService } from "../../member/chase-detail/chase-detail-chart/overread-feedback/overread-feedback.service";

const DATE_TIME_FORMAT = "MM-dd-yyyy hh:mm a";

/**
 * Manager queue of overread feedback challenges (OR I and OR II), split into decisions still owed
 * (Incomplete) and decisions already made (Complete).
 */
@Component({
  selector: "app-clinical-feedback-challenges",
  templateUrl: "./clinical-feedback-challenges.component.html",
  styleUrls: ["./clinical-feedback-challenges.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClinicalFeedbackChallengesComponent implements OnInit, OnDestroy {
  @ViewChild("chaseIdColumn", { static: true }) chaseIdColumn: TemplateRef<ChaseIdComponent>;
  @ViewChild("feedbackStatusColumn", { static: true }) feedbackStatusColumn: TemplateRef<any>;
  @ViewChild(BasicGridComponent, { static: true }) basicGridComponent: BasicGridComponent;
  readonly chaseIdRouteUrl = "/members/chase/:chaseId";
  private sink = new SubSink();
  isComplete = false;
  gridData: FeedbackChallengeQueryItem[] = [];
  gridConfiguration: GridConfiguration;

  constructor(
    private readonly router: Router,
    private readonly overreadFeedbackService: OverreadFeedbackService,
    private readonly gridService: GridService,
    private readonly changeDetector: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.gridConfiguration = this.createGridConfiguration();
    this.loadTab(false);
  }

  ngOnDestroy(): void {
    this.sink.unsubscribe();
  }

  loadTab(isComplete: boolean): void {
    this.isComplete = isComplete;
    this.sink.add(
      this.overreadFeedbackService.getFeedbackChallengeList(new FeedbackChallengeQuerySearch(isComplete))
        .subscribe(data => {
          this.gridData = data ?? [];
          this.changeDetector.markForCheck();
        })
    );
  }

  /** Exports both tabs, matching the Export All behaviour of the Overread Feedback grid. */
  exportAll(): void {
    this.sink.add(
      forkJoin([
        this.overreadFeedbackService.getFeedbackChallengeList(new FeedbackChallengeQuerySearch(false)),
        this.overreadFeedbackService.getFeedbackChallengeList(new FeedbackChallengeQuerySearch(true)),
      ]).subscribe(([incomplete, complete]) => {
        const primeGrid = this.basicGridComponent.primeGrid;
        primeGrid.value = [...(incomplete ?? []), ...(complete ?? [])];
        primeGrid.exportCSV();
        primeGrid.value = this.gridData;
      })
    );
  }

  reviewChallenge(rowData: FeedbackChallengeQueryItem): void {
    const url = this.gridService.generateLink(
      new GridColumnDefinition({ routeUrl: this.chaseIdRouteUrl } as any),
      rowData
    );
    this.router.navigateByUrl(`/${url}`);
  }

  get completeBtnClass(): string {
    return this.isComplete ? "active-button" : "";
  }

  get incompleteBtnClass(): string {
    return this.isComplete ? "" : "active-button";
  }

  private createGridConfiguration(): GridConfiguration {
    const columns = [
      new GridColumnDefinition({ field: "chaseId", template: this.chaseIdColumn, header: "Chase ID", width: "135px" }),
      new GridColumnDefinition({ field: "projectName", header: "Project" }),
      new GridColumnDefinition({ field: "codingUserName", header: "Coder / Abstractor Name" }),
      new GridColumnDefinition({ field: "codingDate", header: "Date & Time Submitted", pipeName: GridPipeName.Date, format: DATE_TIME_FORMAT, timeZone: "local" }),
      new GridColumnDefinition({ field: "qaUserName", header: "Overreader Name" }),
      new GridColumnDefinition({ field: "overreadLevel", header: "Overread Level" }),
      new GridColumnDefinition({ field: "qaDate", header: "Date & Time Overread", pipeName: GridPipeName.Date, format: DATE_TIME_FORMAT, timeZone: "local" }),
      new GridColumnDefinition({ field: "errorCount", header: "# of Errors Found" }),
      new GridColumnDefinition({ field: "chaseOverreadFeedbackStatus", header: "Manager Review" }),
      new GridColumnDefinition({ template: this.feedbackStatusColumn, isSortableColumn: false, header: "Feedback Status", width: "170px" }),
    ];
    return new GridConfiguration({
      columns,
      pageSize: 25,
      showExportAction: false,
      showActionColumn: false,
      pageSizeOptions: [10, 25, 50, 100],
    });
  }
}
